import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { ListPagination, paginate } from "@/components/list-pagination";
import { Item, ItemContent, ItemDescription, ItemTitle } from "@/components/ui/item";
import { StandingNotice } from "@/features/reseller/standing-notice";
import {
  arrangeTlds,
  DEFAULT_PRICING_SEARCH,
  PRICED_FILTERS,
  PricingTable,
  STATUS_FILTERS,
  type PricingSearch,
} from "@/features/pricing/pricing-table";
import { tldPricingQuery } from "@/features/pricing/queries";
import { m } from "@/lib/paraglide/messages.js";

const PAGE_SIZE = 10;

// The search, the filters and the page are route state (guide, rule 16): a link to "the TLDs off
// sale with no price yet" opens just that. Anything the list does not know reads as its default.
const DomainPricingSearch = z.object({
  q: z.string().max(100).optional().catch(undefined),
  status: z.enum(STATUS_FILTERS).optional().catch(undefined),
  priced: z.enum(PRICED_FILTERS).optional().catch(undefined),
  page: z.coerce.number().int().min(1).optional().catch(undefined),
});

// /domains/pricing: what the reseller sells each TLD KilatDomain offers for, whether it sells
// it, and in what order. The API sends the whole list, which the order needs, so the search, the
// filters and the page are applied here. A pending or suspended reseller is told why and reads it
// only: the portal chain refuses it every change anyway (feature_read_only).
export const Route = createFileRoute("/_authenticated/domains/pricing")({
  validateSearch: DomainPricingSearch,
  loader: ({ context }) => context.queryClient.ensureQueryData(tldPricingQuery()),
  staticData: { crumb: m.portal_nav_domain_pricing },
  component: DomainPricingPage,
});

function DomainPricingPage() {
  const { reseller } = Route.useRouteContext();
  const { page: asked = 1, q, status, priced } = Route.useSearch();
  const navigate = Route.useNavigate();
  // Field by field: a value the schema caught is there as undefined, and must not hide the default.
  const search: PricingSearch = {
    q: q ?? DEFAULT_PRICING_SEARCH.q,
    status: status ?? DEFAULT_PRICING_SEARCH.status,
    priced: priced ?? DEFAULT_PRICING_SEARCH.priced,
  };
  const { data: tlds } = useSuspenseQuery(tldPricingQuery());
  const { page, pages, items, range } = paginate(arrangeTlds(tlds, search), asked, PAGE_SIZE);
  // A changed search starts again from the first page; an emptied search box leaves the URL.
  // Replaced, not pushed: Back leaves the list rather than undoing each keystroke.
  const onSearchChange = (next: Partial<PricingSearch>) =>
    navigate({
      search: (prev) => ({ ...prev, ...next, q: (next.q ?? prev.q) || undefined, page: undefined }),
      replace: true,
    });
  const active = reseller.status === "active";
  return (
    <>
      {!active && <StandingNotice status={reseller.status} />}
      <Item>
        <ItemContent>
          <ItemTitle>{m.portal_nav_domain_pricing()}</ItemTitle>
          <ItemDescription>{m.reseller_pricing_description()}</ItemDescription>
        </ItemContent>
      </Item>
      <PricingTable
        tlds={items}
        all={tlds}
        canManage={active}
        search={search}
        onSearchChange={onSearchChange}
        caption={range}
        pagination={<ListPagination page={page} pages={pages} />}
      />
    </>
  );
}
